export function localDateInput(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Converts a date-only input to the final millisecond of that local calendar day. */
export function endOfLocalDay(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) throw new Error('Choose a valid expiry date.')
  const local = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999)
  if (Number.isNaN(local.getTime()) || localDateInput(local) !== date)
    throw new Error('Choose a valid expiry date.')
  return local.toISOString()
}

/** Latest local calendar date whose end is still inside the API's 365-day instant limit. */
export function maxExpiryDate(now = new Date()): string {
  const deadline = now.getTime() + 365 * 24 * 60 * 60 * 1000
  const candidate = new Date(now)
  candidate.setDate(candidate.getDate() + 365)
  while (new Date(endOfLocalDay(localDateInput(candidate))).getTime() > deadline)
    candidate.setDate(candidate.getDate() - 1)
  return localDateInput(candidate)
}

export function factLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID()
  // UUID v4 shape for browsers that do not expose randomUUID yet.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  return [...bytes]
    .map(
      (byte, index) =>
        `${[4, 6, 8, 10].includes(index) ? '-' : ''}${byte.toString(16).padStart(2, '0')}`,
    )
    .join('')
}

export function formatLocalTime(timestamp: string): string {
  const date = new Date(timestamp)
  return `${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)} (${Intl.DateTimeFormat().resolvedOptions().timeZone})`
}

export interface AnswerRow {
  label: string
  value: string
}

/**
 * Flattens one step's saved JSON into labelled rows for the admin view. Step
 * shapes differ, so nested objects become "Parent › Child" labels and list
 * items are numbered; lists of plain values are joined.
 */
export function flattenAnswers(data: unknown, prefix = ''): AnswerRow[] {
  if (data === null || data === undefined || data === '')
    return prefix ? [{ label: prefix, value: '—' }] : []
  if (Array.isArray(data)) {
    if (data.length === 0) return prefix ? [{ label: prefix, value: '—' }] : []
    if (data.every((item) => item === null || typeof item !== 'object'))
      return [{ label: prefix, value: data.map(String).join(', ') }]
    return data.flatMap((item, index) => flattenAnswers(item, `${prefix} ${index + 1}`.trim()))
  }
  if (typeof data === 'object')
    return Object.entries(data as Record<string, unknown>).flatMap(([key, value]) =>
      flattenAnswers(value, prefix ? `${prefix} › ${factLabel(key)}` : factLabel(key)),
    )
  if (typeof data === 'boolean') return [{ label: prefix, value: data ? 'Yes' : 'No' }]
  return [{ label: prefix, value: typeof data === 'string' ? data : JSON.stringify(data) }]
}

/** Human wording for an intake_events row; step titles come from the saved responses. */
export function describeEvent(
  event: string,
  detail: string,
  stepTitles: Map<number, string>,
): string {
  switch (event) {
    case 'opened':
      return 'Opened the link'
    case 'saved': {
      const step = Number(/^step:(\d+)$/.exec(detail)?.[1])
      const title = stepTitles.get(step)
      if (Number.isNaN(step)) return 'Saved a step'
      return title === undefined ? `Saved step ${step + 1}` : `Saved step ${step + 1} — ${title}`
    }
    case 'submitted':
      return 'Submitted the intake'
    case 'reset':
      return 'Answers reset by an admin — the client starts over'
    case 'document_removed':
      return `Removed a document (${detail})`
    case 'document_added_after_submit':
      return `Added a document after submitting (${detail})`
    default:
      return detail ? `${event} (${detail})` : event
  }
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}
